import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import type { SiteSettings } from "../types/settings";
import {
  popularArticles,
  actressRankings,
  makerRankings,
  latestArticles,
  saleItems,
  navCards,
  actressItems,
  actressGuidePages,
  newFaceItems,
  newFaceDetailPages,
  actressDetailPages,
  rankingPageItems,
} from "../mocks/zetsubou";
import { supabase } from "../lib/supabaseClient";

const DEFAULT_SETTINGS: SiteSettings = {
  headerTitle: "Zetsubou.jp",
  headerSubtitle: "エロ動画(AV)完全ガイド",
  headerUrl: "https://zetsubou.jp/",
  heroText:
    "無限にあるエロ動画(AV)から\n目的地までなんとか最短で辿りつけないか？\nというコンセプトを追求",
  heroImageUrl: "https://zetsubou.jp/wp-content/uploads/zetsubou-angel.jpg",
  heroNavCards: navCards,
  footerTitle: "Zetsubou.jp",
  footerCopyright: "© 2018-2026 Zetsubou.jp.",
  footerAdminLink: true,
  navItems: [
    { id: "home", label: "HOME", href: "https://h-information.net/" },
    {
      id: "contents",
      label: "サイトコンテンツ",
      href: "https://h-information.net/",
      children: [
        {
          label: "Hな情報（総合）",
          href: "https://zetsubou.jp/av/",
          children: [
            { label: "AV女優ガイド", href: "https://zetsubou.jp/av/actress/" },
            { label: "VRAVガイド", href: "https://zetsubou.jp/av/vrav/" },
            { label: "新人女優ガイド", href: "https://zetsubou.jp/av/newface/" },
            { label: "マニアックAVガイド", href: "https://zetsubou.jp/av/maniac/" },
          ],
        },
      ],
    },
    {
      id: "sites",
      label: "運営サイト",
      href: "https://z-groups.jp/",
      children: [
        { label: "真・絶望だけを愛して", href: "https://av.zetsubou.org/" },
        { label: "絶望だけを愛して", href: "https://zetsubou.love/" },
        { label: "絶愛2\'nd", href: "https://2nd-z.com/" },
        { label: "絶望AVメーカー", href: "https://zlove.jp/" },
        { label: "絶望AV女優", href: "https://zlovers.jp/" },
      ],
    },
    {
      id: "contact",
      label: "お問い合わせ",
      href: "https://z-group.jp/contact/",
    },
  ],
  sections: [
    { id: "hero", label: "ヒーローエリア", enabled: true, order: 0 },
    { id: "ranking-popular", label: "人気記事ランキング", enabled: true, order: 1 },
    { id: "ranking-actress", label: "人気女優ランキング", enabled: true, order: 2 },
    { id: "ranking-maker", label: "AVメーカーランキング", enabled: true, order: 3 },
    { id: "latest-articles", label: "最新記事", enabled: true, order: 4 },
    { id: "sale-info", label: "セール情報", enabled: true, order: 5 },
    { id: "author-profile", label: "著者プロフィール", enabled: true, order: 6 },
    { id: "social-link", label: "SNSリンク", enabled: true, order: 7 },
  ],
  rankingSections: [
    {
      id: "ranking-popular",
      title: "人気記事ランキング",
      items: popularArticles,
      moreHref: "https://av.zetsubou.org/popular-list-ranking/",
      moreLabel: "ランキングの続きはこちら",
    },
    {
      id: "ranking-actress",
      title: "人気女優ランキング",
      items: actressRankings,
      moreHref: "https://zlovers.jp/actress-ranking/",
      moreLabel: "続きはこちら",
    },
    {
      id: "ranking-maker",
      title: "AVメーカーランキング",
      items: makerRankings,
      moreHref: "https://zlove.jp/all-of-avmaker/",
      moreLabel: "続きはこちら",
    },
  ],
  latestArticlesTitle: "最新&更新記事",
  latestArticles,
  saleItems,
  saleMoreHref: "https://av.zetsubou.org/fanza-sale-info/",
  authorName: "Zetsubou",
  authorBio:
    "2018年からアダルト動画レビューサイトを運営。\n運営歴7年、累計訪問者は3000万人を突破。\n実際に視聴した作品をもとに、リアルなレビューと考察を発信しています。",
  authorImageUrl: "https://zetsubou.jp/wp-content/uploads/prof-icon-z.webp",
  authorSiteUrl: "https://z-group.jp",
  authorTwitterUrl: "https://x.com/zetsubou_love",
  socialLinkText: "Xでも情報発信中！",
  socialLinkHref: "https://x.com/zetsubou_love",
  socialLinkSub:
    "あまりSNSに馴染めてない人間ですが、いいねやフォローしていただけると励みになります",
  actressPageTitle: "AV女優ガイド",
  actressPageSubtitle: "人気女優の作品情報・レビューまとめ",
  actressItems,
  actressGuidePages,
  newFacePageTitle: "新人女優ガイド",
  newFacePageSubtitle: "最新デビュー女優の情報まとめ",
  newFaceItems,
  newFaceDetailPages,
  actressDetailPages,
  rankingPageTitle: "人気ランキング",
  rankingPageSubtitle: "人気記事・女優・メーカーの最新ランキング",
  rankingPageItems,
};

const SETTINGS_ROW_ID = 1;
const LEGACY_STORAGE_KEY = "zetsubou_site_settings";

// base64画像（data:で始まる文字列）を除去してSupabaseに安全に保存できる形に変換
function stripBase64(val: string): string {
  return val.startsWith("data:") ? "" : val;
}

function sanitizeSettingsForSupabase(s: SiteSettings): SiteSettings {
  return {
    ...s,
    heroImageUrl: stripBase64(s.heroImageUrl),
    authorImageUrl: stripBase64(s.authorImageUrl),
    heroNavCards: s.heroNavCards.map((c) => ({ ...c, img: stripBase64(c.img) })),
    rankingSections: s.rankingSections.map((rs) => ({
      ...rs,
      items: rs.items.map((item) => ({ ...item, img: stripBase64(item.img) })),
    })),
    latestArticles: s.latestArticles.map((a) => ({ ...a, img: stripBase64(a.img) })),
    saleItems: s.saleItems.map((si) => ({ ...si, img: stripBase64(si.img) })),
  };
}

interface SiteSettingsContextValue {
  settings: SiteSettings;
  updateSettings: (patch: Partial<SiteSettings>) => void;
  resetSettings: () => void;
  saveSettings: () => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
  isLoading: boolean;
  saveError: string | null;
}

const SiteSettingsContext = createContext<SiteSettingsContextValue | null>(null);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<string>(
    JSON.stringify(DEFAULT_SETTINGS)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Supabaseから設定を読み込む（localStorageからの移行も対応）
  useEffect(() => {
    async function fetchSettings() {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("site_settings")
          .select("settings")
          .eq("id", SETTINGS_ROW_ID)
          .maybeSingle();

        if (error) throw error;

        if (data?.settings) {
          // Supabaseにデータがある場合はそれを使う
          const loaded = { ...DEFAULT_SETTINGS, ...(data.settings as Partial<SiteSettings>) };
          setSettings(loaded);
          setSavedSettings(JSON.stringify(loaded));
        } else {
          // Supabaseにデータがない場合、localStorageから移行を試みる
          const legacyRaw = localStorage.getItem(LEGACY_STORAGE_KEY);
          if (legacyRaw) {
            try {
              const legacyParsed = JSON.parse(legacyRaw) as Partial<SiteSettings>;
              const migrated = { ...DEFAULT_SETTINGS, ...legacyParsed };

              // base64画像を除外（URL形式のみ保持）
              const cleanedSettings = sanitizeSettingsForSupabase(migrated);

              // Supabaseに自動保存
              const { error: saveErr } = await supabase
                .from("site_settings")
                .upsert({
                  id: SETTINGS_ROW_ID,
                  settings: cleanedSettings,
                  updated_at: new Date().toISOString(),
                });

              if (!saveErr) {
                // localStorageを削除
                localStorage.removeItem(LEGACY_STORAGE_KEY);
                setSettings(cleanedSettings);
                setSavedSettings(JSON.stringify(cleanedSettings));
              } else {
                setSettings(migrated);
                setSavedSettings(JSON.stringify(migrated));
              }
            } catch {
              // localStorageのパースに失敗した場合はデフォルトを使う
            }
          }
        }
      } catch (err) {
        console.error("設定の読み込みに失敗しました:", err);
        // フォールバック: localStorageを確認
        try {
          const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as Partial<SiteSettings>;
            setSettings({ ...DEFAULT_SETTINGS, ...parsed });
          }
        } catch {
          // ignore
        }
      } finally {
        setIsLoading(false);
      }
    }

    fetchSettings();
  }, []);

  const updateSettings = useCallback((patch: Partial<SiteSettings>) => {
    setSettings((prev) => ({ ...prev, ...patch }));
    setSaveError(null);
  }, []);

  const saveSettings = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from("site_settings")
        .upsert({ id: SETTINGS_ROW_ID, settings, updated_at: new Date().toISOString() })
        .eq("id", SETTINGS_ROW_ID);

      if (error) throw error;

      setSavedSettings(JSON.stringify(settings));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "保存に失敗しました";
      setSaveError(msg);
      console.error("設定の保存に失敗しました:", err);
    } finally {
      setIsSaving(false);
    }
  }, [settings]);

  const resetSettings = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          id: SETTINGS_ROW_ID,
          settings: DEFAULT_SETTINGS,
          updated_at: new Date().toISOString(),
        })
        .eq("id", SETTINGS_ROW_ID);

      if (error) throw error;

      setSettings(DEFAULT_SETTINGS);
      setSavedSettings(JSON.stringify(DEFAULT_SETTINGS));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "リセットに失敗しました";
      setSaveError(msg);
      console.error("設定のリセットに失敗しました:", err);
    } finally {
      setIsSaving(false);
    }
  }, []);

  const isDirty = JSON.stringify(settings) !== savedSettings;

  return (
    <SiteSettingsContext.Provider
      value={{ settings, updateSettings, resetSettings, saveSettings, isDirty, isSaving, isLoading, saveError }}
    >
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) throw new Error("useSiteSettings must be used within SiteSettingsProvider");
  return ctx;
}

export { DEFAULT_SETTINGS };
